/**
 * The post-upload "email yourself a link" prompt, as a pure rule.
 *
 * Extracted from UploadScreen because the ASK-ONCE behaviour is the part that
 * regresses silently: the batch-done modal fires on every completed batch, so a
 * customer uploading three lots would be asked three times, and the only symptom
 * is an annoyed customer nobody hears from.
 *
 * Context: 408 abandoned drafts held ONE email between them, because the only
 * save prompt lived on the cart screen and just 36 of those drafts ever reached
 * it. 116 of them uploaded at least one photo, so the batch-done modal is the
 * widest point in the funnel where there is anything worth saving.
 */

/** Same shape SaveOrderModal validates with: one prompt must not accept what the other rejects. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface SavePromptInput {
  /** The prompt has already had its turn this order (saved, skipped or continued past). */
  seen: boolean;
  /** The project already carries an address, so there is nothing to ask for. */
  savedEmail: string | null;
  /** Whatever is currently in the field. */
  typed: string;
}

export interface SavePromptState {
  /** Render the field and the offer copy. */
  ask: boolean;
  /** A valid address is present, so the primary button should save before it continues. */
  wantsSave: boolean;
  /** They typed something that is not an address: correct it rather than binning it. */
  invalid: boolean;
}

export function savePromptState({ seen, savedEmail, typed }: SavePromptInput): SavePromptState {
  const ask = !seen && !savedEmail;
  const value = typed.trim();
  const valid = EMAIL_RE.test(value);
  return {
    ask,
    wantsSave: ask && valid,
    // Empty is not invalid — an ignored field is the intended way to skip, and
    // blocking on it would turn an optional prompt into a gate.
    invalid: ask && value.length > 0 && !valid,
  };
}
